/**
 * Cards for a headless conversation agent's capture frames — what the person
 * on the other end of the email/SMS thread actually received, and the decision
 * recorded on the alert. Models come from conversationMessageCards.ts; this
 * module only exports components (react-refresh/only-export-components).
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  decisionHeadline,
  decisionSourceLabel,
  type ConversationDecisionData,
  type SentMessageData,
} from './conversationMessageCards';
import styles from './ConversationMessageCard.module.css';

export function SentMessageCard({ message }: { message: SentMessageData }) {
  return (
    <div className={styles.card} data-testid="sent-message-card">
      <div className={styles.header}>
        <span className={styles.icon} aria-hidden="true">
          ✉️
        </span>
        <span className={styles.title}>Sent to the salesperson</span>
      </div>
      {message.markdown && (
        <div className={styles.body}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.markdown}</ReactMarkdown>
        </div>
      )}
      {message.sms && (
        <div className={styles.sms} data-testid="sent-message-sms">
          <span className={styles.smsLabel}>Text message</span>
          <span className={styles.smsBody}>{message.sms}</span>
        </div>
      )}
    </div>
  );
}

export function ConversationDecisionCard({
  decision,
}: {
  decision: ConversationDecisionData;
}) {
  const approve = decision.decision === 'approve';
  return (
    <div
      className={`${styles.card} ${approve ? styles.approve : styles.deny}`}
      data-testid="conversation-decision-card"
    >
      <div className={styles.header}>
        <span className={styles.icon} aria-hidden="true">
          {approve ? '✅' : '⛔'}
        </span>
        <span className={styles.title}>{decisionHeadline(decision)}</span>
        <span className={styles.source}>{decisionSourceLabel(decision.source)}</span>
      </div>
      <blockquote className={styles.basis}>{decision.basis}</blockquote>
    </div>
  );
}
